import React, { useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { AppDialog, FormField, FormRow, FormSection, RowActions, emptyMessage, rowActionsTableStyle } from "../ui";

import {
  useGetBenefitPartnerLocationsQuery,
  useCreateBenefitPartnerLocationMutation,
  useUpdateBenefitPartnerLocationMutation,
  useDeleteBenefitPartnerLocationMutation,
} from "../../store/services/benefits";
import {
  BenefitLocation,
  BenefitLocationInput,
  BenefitLocationSchedule,
} from "../../models/benefits";
import { yupConstants } from "../../utils/constants/yup";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { BenefitLocationMapPicker } from "./BenefitLocationMapPicker";

/**
 * `schedule` viaja como string opaco: el BO guarda JSON
 * {weekdays, saturday, sunday}. Parseo defensivo: si no es JSON,
 * el texto crudo va a weekdays.
 */
const parseSchedule = (schedule: string | null | undefined): BenefitLocationSchedule => {
  const empty: BenefitLocationSchedule = {
    weekdays: null,
    saturday: null,
    sunday: null,
  };
  if (!schedule) return empty;
  try {
    const parsed = JSON.parse(schedule);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return {
        weekdays: typeof parsed.weekdays === "string" ? parsed.weekdays : null,
        saturday: typeof parsed.saturday === "string" ? parsed.saturday : null,
        sunday: typeof parsed.sunday === "string" ? parsed.sunday : null,
      };
    }
  } catch (_) {}
  return { ...empty, weekdays: schedule };
};

const scheduleSummary = (schedule: string | null | undefined): string => {
  const parsed = parseSchedule(schedule);
  const parts: string[] = [];
  if (parsed.weekdays) parts.push(`L a V: ${parsed.weekdays}`);
  if (parsed.saturday) parts.push(`Sáb: ${parsed.saturday}`);
  if (parsed.sunday) parts.push(`Dom: ${parsed.sunday}`);
  return parts.length > 0 ? parts.join(" · ") : "-";
};

interface LocationFormValues {
  address: string;
  latitude: string;
  longitude: string;
  phoneNumber: string;
  weekdays: string;
  saturday: string;
  sunday: string;
}

interface LocationFormDialogProps {
  partnerId: string;
  /** MT-011 — Tenant del partner: viaja como header X-Tenant-Context. */
  tenantId?: string | null;
  location?: BenefitLocation;
  onHide: () => void;
}

const LocationFormDialog = ({
  partnerId,
  tenantId,
  location,
  onHide,
}: LocationFormDialogProps) => {
  const addressInputRef = useRef<HTMLInputElement>(null);

  const [createLocation, { isLoading: isCreating }] =
    useCreateBenefitPartnerLocationMutation();
  const [updateLocation, { isLoading: isUpdating }] =
    useUpdateBenefitPartnerLocationMutation();

  const isSaving = isCreating || isUpdating;

  const initialSchedule = useMemo(
    () => parseSchedule(location?.schedule),
    [location]
  );

  const {
    handleSubmit,
    submitForm,
    touched,
    errors,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
  } = useFormik<LocationFormValues>({
    initialValues: {
      address: location?.address || "",
      latitude: location?.latitude || "",
      longitude: location?.longitude || "",
      phoneNumber: location?.phoneNumber || "",
      weekdays: initialSchedule.weekdays || "",
      saturday: initialSchedule.saturday || "",
      sunday: initialSchedule.sunday || "",
    },
    validationSchema: Yup.object({
      address: Yup.string().trim().required(yupConstants().required),
      latitude: Yup.string().required(yupConstants().required),
      longitude: Yup.string().required(yupConstants().required),
    }),
    onSubmit: async (vals) => {
      const weekdays = vals.weekdays.trim() || null;
      const saturday = vals.saturday.trim() || null;
      const sunday = vals.sunday.trim() || null;
      const schedule =
        weekdays || saturday || sunday
          ? JSON.stringify({ weekdays, saturday, sunday })
          : null;

      const payload: BenefitLocationInput = {
        address: vals.address.trim(),
        latitude: String(vals.latitude),
        longitude: String(vals.longitude),
        schedule,
        phoneNumber: vals.phoneNumber.trim() || null,
      };

      try {
        if (location) {
          await updateLocation({
            partnerId,
            tenantId,
            id: location.id,
            ...payload,
          }).unwrap();
          toast.success("Ubicación actualizada");
        } else {
          await createLocation({ partnerId, tenantId, ...payload }).unwrap();
          toast.success("Ubicación creada");
        }
        onHide();
      } catch (err) {
        toast.error(
          getAdminFacingApiErrorMessage(err, "No se pudo guardar la ubicación")
        );
      }
    },
  });

  const fieldError = (name: keyof LocationFormValues) =>
    touched[name] && errors[name] ? errors[name] : undefined;

  return (
    <AppDialog
      variant="form"
      visible
      header={location ? "Editar Ubicación" : "Agregar Ubicación"}
      style={{ width: "min(720px, 95vw)" }}
      onHide={onHide}
      primaryLabel={location ? "Guardar cambios" : "Agregar Ubicación"}
      onPrimary={() => void submitForm()}
      loading={isSaving}
    >
      {/* El <form> se mantiene para que Enter siga guardando; el botón
          primario del footer de AppDialog dispara el mismo submit. */}
      <form onSubmit={handleSubmit} className="p-fluid">
        <FormRow columns={3}>
          <FormField label="Dirección" htmlFor="address" required span="full" error={fieldError("address")}>
            {/* Input nativo: el autocomplete de Places necesita el ref del DOM. */}
            <input
              ref={addressInputRef}
              id="address"
              name="address"
              type="text"
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`p-inputtext p-component w-full${
                fieldError("address") ? " p-invalid" : ""
              }`}
              disabled={isSaving}
              placeholder="Escribí una dirección o elegí una sugerencia"
              autoComplete="off"
            />
          </FormField>
          <FormField
            label="Mapa (buscá una dirección, hacé click o arrastrá el marcador)"
            span="full"
          >
            <BenefitLocationMapPicker
              latitude={values.latitude}
              longitude={values.longitude}
              addressInputRef={addressInputRef}
              onPick={({ latitude, longitude, address }) => {
                setFieldValue("latitude", latitude);
                setFieldValue("longitude", longitude);
                if (address) setFieldValue("address", address);
              }}
            />
          </FormField>
          <FormField label="Latitud" htmlFor="latitude" required error={fieldError("latitude")}>
            <InputText id="latitude" placeholder="Se completa con el mapa" value={values.latitude} disabled readOnly />
          </FormField>
          <FormField label="Longitud" htmlFor="longitude" required error={fieldError("longitude")}>
            <InputText id="longitude" placeholder="Se completa con el mapa" value={values.longitude} disabled readOnly />
          </FormField>
          <FormField label="Teléfono" htmlFor="phoneNumber">
            <InputText
              id="phoneNumber"
              name="phoneNumber"
              value={values.phoneNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSaving}
              placeholder="Ej: +598 99 123 456"
            />
          </FormField>
          <FormField label="Horario lunes a viernes" htmlFor="weekdays">
            <InputText
              id="weekdays"
              name="weekdays"
              value={values.weekdays}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSaving}
              placeholder="Ej: 9:00 - 18:00"
            />
          </FormField>
          <FormField label="Horario sábado" htmlFor="saturday">
            <InputText
              id="saturday"
              name="saturday"
              value={values.saturday}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSaving}
              placeholder="Ej: 9:00 - 18:00"
            />
          </FormField>
          <FormField label="Horario domingo" htmlFor="sunday">
            <InputText
              id="sunday"
              name="sunday"
              value={values.sunday}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSaving}
              placeholder="Ej: 9:00 - 18:00"
            />
          </FormField>
        </FormRow>
        {/* Submit oculto: Enter dentro de un campo guarda, como antes. */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </AppDialog>
  );
};

interface BenefitLocationsManagerProps {
  partnerId: string;
  /**
   * MT-011 — Tenant del partner. Las ubicaciones se operan siempre sobre el
   * tenant dueño del partner, no sobre el del selector superior.
   */
  tenantId?: string | null;
  /** Número de la sección dentro del formulario del partner. */
  sectionNumber?: number;
}

/**
 * Sección "Ubicaciones" del partner. Renderiza su propia <FormSection> para que
 * el alta quede como `+` circular en el encabezado (§6.1), igual que el resto.
 */
export const BenefitLocationsManager = ({
  partnerId,
  tenantId,
  sectionNumber,
}: BenefitLocationsManagerProps) => {
  const { data: locations, isLoading } = useGetBenefitPartnerLocationsQuery({
    partnerId,
    tenantId,
  });
  const [deleteLocation, { isLoading: isDeleting }] =
    useDeleteBenefitPartnerLocationMutation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<BenefitLocation | undefined>();
  const [locationToDelete, setLocationToDelete] = useState<BenefitLocation | undefined>();

  const openCreateDialog = () => {
    setEditingLocation(undefined);
    setIsDialogOpen(true);
  };

  const openEditDialog = (location: BenefitLocation) => {
    setEditingLocation(location);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingLocation(undefined);
  };

  const hideDeleteDialog = () => setLocationToDelete(undefined);

  const handleDeleteLocation = (location: BenefitLocation) => {
    deleteLocation({ partnerId, tenantId, id: location.id })
      .unwrap()
      .then(() => toast.success("Ubicación eliminada"))
      .catch((err) =>
        toast.error(
          getAdminFacingApiErrorMessage(err, "No se pudo eliminar la ubicación")
        )
      )
      .finally(() => hideDeleteDialog());
  };

  const scheduleBodyTemplate = (location: BenefitLocation) => (
    <span>{scheduleSummary(location.schedule)}</span>
  );

  const actionsBodyTemplate = (location: BenefitLocation) => (
    <RowActions
      actions={[
        { icon: "pi pi-pencil", label: "Editar", onClick: () => openEditDialog(location) },
        {
          icon: "pi pi-trash",
          label: "Eliminar",
          severity: "danger",
          onClick: () => setLocationToDelete(location),
        },
      ]}
    />
  );

  return (
    <FormSection
      title="Ubicaciones"
      number={sectionNumber}
      titleAction={
        <Button
          type="button"
          icon="pi pi-plus"
          rounded
          size="small"
          aria-label="Agregar ubicación"
          tooltip="Agregar ubicación"
          tooltipOptions={{ position: "top" }}
          onClick={openCreateDialog}
        />
      }
    >
      <DataTable
        style={rowActionsTableStyle("1rem", "6.5rem")}
        value={locations ?? []}
        loading={isLoading || isDeleting}
        emptyMessage={emptyMessage("ubicaciones")}
        stripedRows
        dataKey="id"
      >
        <Column field="address" header="Dirección" style={{ minWidth: "16rem" }} />
        <Column field="phoneNumber" header="Teléfono" style={{ minWidth: "10rem" }} />
        <Column
          header="Horarios"
          body={scheduleBodyTemplate}
          style={{ minWidth: "14rem" }}
        />
        <Column
          header={<span className="row-actions-label">Acciones</span>}
          // Bloque contra el borde derecho con el mismo margen que la primera columna
          // a la izquierda; título e íconos centrados dentro del bloque.
          headerClassName="row-actions-th"
          bodyClassName="row-actions-cell"
          body={actionsBodyTemplate}
          style={{ minWidth: "8rem" }}
          exportable={false}
        />
      </DataTable>

      {isDialogOpen && (
        <LocationFormDialog
          partnerId={partnerId}
          tenantId={tenantId}
          location={editingLocation}
          onHide={closeDialog}
        />
      )}

      <AppDialog
        variant="destructive"
        visible={!!locationToDelete}
        onHide={hideDeleteDialog}
        header="Eliminar ubicación"
        primaryLabel="Eliminar Ubicación"
        onPrimary={() => locationToDelete && handleDeleteLocation(locationToDelete)}
        loading={isDeleting}
      >
        <span>
          ¿Estás seguro de que querés eliminar la ubicación{" "}
          <strong>{locationToDelete?.address || ""}</strong>?
        </span>
      </AppDialog>
    </FormSection>
  );
};
